import React, { useState, useEffect } from "react";
import { makeRequest2 } from "../../../../../utils/makeRequest";
import { getItem } from "../../../../../utils/localStorage";
import { toastify } from "../../../../../utils/toast";

const PendingApprovalsModal = ({
  show,
  onClose,
  facilityId,
  onApprovalSuccess,
  pendingMetadata = [], // Accept pending metadata as props
  pendingWallets = [], // Accept pending wallets as props
  totalPendingAmount = 0, // Accept total pending amount as props
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [approvingLoading, setApprovingLoading] = useState(false);
  const [rejectModalShow, setRejectModalShow] = useState(false);
  const [itemToReject, setItemToReject] = useState(null);

  // Local state for metadata (for when we need to update the list after approvals)
  const [localPendingMetadata, setLocalPendingMetadata] = useState([]);

  // Filter states
  const [filters, setFilters] = useState({
    status: "pending", // Default to pending
    dateFrom: "",
    dateTo: "",
    invoiceNumber: "",
  });

  // Available status options
  const statusOptions = [
    { value: "", label: "All Statuses" },
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "cancelled", label: "Cancelled" },
    { value: "paid", label: "Paid" },
    { value: "partial", label: "Partial" },
  ];

  // Enhanced user retrieval - check multiple possible keys and handle promises
  const getUserId = () => {
    // Try different localStorage keys where user data might be stored
    const possibleKeys = [
      "userId",
      "user",
      "loggedInUser",
      "currentUser",
      "authUser",
    ];

    for (const key of possibleKeys) {
      try {
        const value = localStorage.getItem(key);
        if (value && value !== "null" && value !== "undefined") {
          console.log(`✅ Found user data in localStorage['${key}']:`, value);

          // If it's already a string ID, return it
          if (
            typeof value === "string" &&
            value.length > 10 &&
            !value.startsWith("{")
          ) {
            return value;
          }

          // Try to parse if it looks like JSON
          try {
            const parsed = JSON.parse(value);
            if (parsed && typeof parsed === "object") {
              const userId =
                parsed._id || parsed.id || parsed.userId || parsed.user_id;
              if (userId) {
                console.log(`✅ Extracted user ID from ${key}:`, userId);
                return userId;
              }
            }
          } catch (parseError) {
            // If it's not JSON, might be a direct ID string
            return value;
          }
        }
      } catch (error) {
        console.log(`❌ Error checking ${key}:`, error);
      }
    }

    // Fallback: try the getItem function (but handle if it returns a promise)
    try {
      const userFromGetItem = getItem("user");
      console.log("🔧 getItem('user') result:", userFromGetItem);

      if (userFromGetItem && typeof userFromGetItem.then === "function") {
        // It's a promise - we'll handle this differently
        console.log("⚠️ getItem returned a promise, cannot use synchronously");
        return null;
      } else if (userFromGetItem) {
        const userId =
          userFromGetItem._id || userFromGetItem.id || userFromGetItem.userId;
        if (userId) return userId;
      }
    } catch (error) {
      console.log("❌ Error with getItem function:", error);
    }

    console.log("❌ No valid user ID found");
    return null;
  };

  // Get user ID
  const approvedBy = getUserId();

  // Filter metadata based on current filters
  const getFilteredMetadata = () => {
    let filtered = [...localPendingMetadata];

    // Status filter
    if (filters.status) {
      filtered = filtered.filter((item) => item.status === filters.status);
    }

    // Date range filter
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filtered = filtered.filter((item) => {
        const itemDate = new Date(item.createdAt);
        return itemDate >= fromDate;
      });
    }

    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter((item) => {
        const itemDate = new Date(item.createdAt);
        return itemDate <= toDate;
      });
    }

    // Invoice number filter
    if (filters.invoiceNumber) {
      filtered = filtered.filter(
        (item) =>
          item.invoiceNumber
            ?.toLowerCase()
            .includes(filters.invoiceNumber.toLowerCase()) ||
          item.invoiceId
            ?.toLowerCase()
            .includes(filters.invoiceNumber.toLowerCase()),
      );
    }

    return filtered;
  };

  const filteredMetadata = getFilteredMetadata();

  // Update local metadata when props change
  useEffect(() => {
    if (show) {
      setLocalPendingMetadata(pendingMetadata);
      setSelectedItems([]);
    }
  }, [show, pendingMetadata]);

  // Calculate summary from filtered metadata
  const summary = {
    total: filteredMetadata.length,
    totalAmount: filteredMetadata.reduce(
      (sum, item) => sum + (item.amount || 0),
      0,
    ),
    avgAmount:
      filteredMetadata.length > 0
        ? filteredMetadata.reduce((sum, item) => sum + (item.amount || 0), 0) /
          filteredMetadata.length
        : 0,
  };

  // Handle filter changes
  const handleFilterChange = (filterKey, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: value,
    }));
    // Clear selection when filters change
    setSelectedItems([]);
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      status: "pending",
      dateFrom: "",
      dateTo: "",
      invoiceNumber: "",
    });
    setSelectedItems([]);
  };

  // Handle select/deselect items (now works with filtered data)
  const handleSelectItem = (metadataId) => {
    setSelectedItems((prev) => {
      if (prev.includes(metadataId)) {
        return prev.filter((id) => id !== metadataId);
      } else {
        return [...prev, metadataId];
      }
    });
  };

  // Handle select all (now works with filtered data)
  const handleSelectAll = () => {
    if (selectedItems.length === filteredMetadata.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredMetadata.map((item) => item._id));
    }
  };

  // Handle single approval
  const handleSingleApproval = async (metadataId, notes = "") => {
    if (!approvedBy) {
      toastify("User not found. Please login again.", "error");
      return;
    }

    const metadata = localPendingMetadata.find(
      (item) => item._id === metadataId,
    );
    if (!metadata) {
      toastify("Metadata item not found", "error");
      return;
    }

    try {
      setApprovingLoading(true);

      const requestPayload = {
        approvedBy,
        notes: notes || "Single approval from UI",
      };

      // Use the walletId from the metadata to make the approval call
      const response = await makeRequest2(
        `/api/app/wallet/${facilityId}/metadata/${metadataId}/approve`,
        "PUT",
        requestPayload,
      );

      if (response.success) {
        toastify("Transaction approved successfully!", "success");

        // Remove approved item from local list
        setLocalPendingMetadata((prev) =>
          prev.filter((item) => item._id !== metadataId),
        );
        setSelectedItems((prev) => prev.filter((id) => id !== metadataId));

        // Call success callback to refresh parent data
        if (onApprovalSuccess) onApprovalSuccess();
      } else {
        console.error("❌ Approval failed:", response);
        toastify(
          response.error || response.message || "Failed to approve transaction",
          "error",
        );
      }
    } catch (error) {
      console.error("❌ Error approving transaction:", error);
      toastify(`Error approving transaction: ${error.message}`, "error");
    } finally {
      setApprovingLoading(false);
    }
  };

  // Handle bulk approval (works with selected filtered items)
  const handleBulkApproval = async () => {
    if (selectedItems.length === 0) {
      toastify("Please select items to approve", "warning");
      return;
    }

    if (!approvedBy) {
      console.error("❌ No approvedBy value found for bulk approval");
      toastify("User not found. Please login again.", "error");
      return;
    }

    try {
      setApprovingLoading(true);

      // For simplicity, let's do individual approvals for each selected item
      let totalApproved = 0;
      let totalFailed = 0;
      const approvedIds = [];

      for (const metadataId of selectedItems) {
        try {
          const requestPayload = {
            approvedBy,
            notes: "Bulk approval from UI",
          };

          const response = await makeRequest2(
            `/api/app/wallet/${facilityId}/metadata/${metadataId}/approve`,
            "PUT",
            requestPayload,
          );

          if (response.success) {
            totalApproved++;
            approvedIds.push(metadataId);
          } else {
            totalFailed++;
            console.error(`❌ Failed to approve ${metadataId}:`, response);
          }
        } catch (error) {
          totalFailed++;
          console.error(`❌ Error approving ${metadataId}:`, error);
        }
      }

      // Show results
      if (totalApproved > 0) {
        toastify(
          `Successfully approved ${totalApproved} transactions!`,
          "success",
        );
      }

      if (totalFailed > 0) {
        toastify(`${totalFailed} transactions failed to approve`, "warning");
      }

      // Remove approved items from local list
      setLocalPendingMetadata((prev) =>
        prev.filter((item) => !approvedIds.includes(item._id)),
      );
      setSelectedItems([]);

      // Call success callback to refresh parent data
      if (onApprovalSuccess) onApprovalSuccess();
    } catch (error) {
      console.error("❌ Error bulk approving:", error);
      toastify("Error approving transactions", "error");
    } finally {
      setApprovingLoading(false);
    }
  };

  // Handle rejection
  const handleRejectTransaction = async (metadataId, rejectionReason) => {
    console.log("🔄 Starting rejection process for:", metadataId);
    console.log("👤 Using rejectedBy:", approvedBy);

    if (!approvedBy) {
      console.error("❌ No approvedBy value found for rejection");
      toastify("User not found. Please login again.", "error");
      return;
    }

    const metadata = localPendingMetadata.find(
      (item) => item._id === metadataId,
    );
    if (!metadata) {
      toastify("Metadata item not found", "error");
      return;
    }

    try {
      setApprovingLoading(true);

      const requestPayload = {
        rejectedBy: approvedBy,
        rejectionReason,
      };

      console.log("📤 Sending rejection request:", {
        url: `/api/app/wallet/${facilityId}/metadata/${metadataId}/reject`,
        payload: requestPayload,
      });

      const response = await makeRequest2(
        `/api/app/wallet/${facilityId}/metadata/${metadataId}/reject`,
        "PUT",
        requestPayload,
      );

      console.log("📥 Rejection response:", response);

      if (response.success) {
        toastify("Transaction rejected successfully!", "success");

        // Remove rejected item from local list
        setLocalPendingMetadata((prev) =>
          prev.filter((item) => item._id !== metadataId),
        );
        setSelectedItems((prev) => prev.filter((id) => id !== metadataId));

        setRejectModalShow(false);
        setItemToReject(null);

        // Call success callback to refresh parent data
        if (onApprovalSuccess) onApprovalSuccess();
      } else {
        console.error("❌ Rejection failed:", response);
        toastify(
          response.error || response.message || "Failed to reject transaction",
          "error",
        );
      }
    } catch (error) {
      console.error("❌ Error rejecting transaction:", error);
      toastify(`Error rejecting transaction: ${error.message}`, "error");
    } finally {
      setApprovingLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid Date";
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get wallet type display name
  const getWalletTypeDisplayName = (walletType) => {
    const walletTypes = {
      levy: "Levy",
      propertyManagement: "Property Management",
      lease: "Lease",
      utility: "Utility",
      vas: "Value Added Services",
    };
    return walletTypes[walletType] || walletType || "General";
  };

  // Get status badge style
  const getStatusBadgeStyle = (status) => {
    const baseStyle = {
      padding: "4px 8px",
      borderRadius: "4px",
      fontSize: "12px",
      fontWeight: "bold",
      display: "inline-block",
      textAlign: "center",
      minWidth: "60px",
    };

    switch (status) {
      case "paid":
        return { ...baseStyle, backgroundColor: "#d4edda", color: "#155724" };
      case "approved":
        return { ...baseStyle, backgroundColor: "#d1ecf1", color: "#0c5460" };
      case "pending":
        return { ...baseStyle, backgroundColor: "#fff3cd", color: "#856404" };
      case "partial":
        return { ...baseStyle, backgroundColor: "#e2e3e5", color: "#383d41" };
      case "rejected":
        return { ...baseStyle, backgroundColor: "#f8d7da", color: "#721c24" };
      case "cancelled":
        return { ...baseStyle, backgroundColor: "#f8f9fa", color: "#495057" };
      default:
        return { ...baseStyle, backgroundColor: "#e2e3e5", color: "#383d41" };
    }
  };

  if (!show) return null;

  return (
    <>
      <div
        className="modal fade show"
        style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
      >
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header d-flex justify-content-between align-items-center py-3 px-4">
              <h5 className="modal-title">
                <i className="fa fa-clock mr-2 text-warning"></i>
                Transaction Approvals Management
              </h5>

              <button
                type="button"
                className="btn btn-light btn-sm rounded-circle p-2"
                onClick={onClose}
                style={{
                  width: "40px",
                  height: "40px",
                  border: "1px solid #dee2e6",
                  fontSize: "18px",
                  lineHeight: "1",
                }}
              >
                <span className="text-muted">&times;</span>
              </button>
            </div>

            <div className="modal-body">
              {/* User Authentication Warning */}
              {!approvedBy && (
                <div className="alert alert-danger mb-3">
                  <i className="fa fa-exclamation-triangle mr-2"></i>
                  <strong>Authentication Issue:</strong> No valid user ID found.
                  Please ensure you are logged in properly.
                  <br />
                  <small>
                    Available localStorage keys:{" "}
                    {Object.keys(localStorage).join(", ")}
                  </small>
                </div>
              )}

              {/* Filters Section */}
              <div className="card mb-4">
                <div className="card-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">
                      <i className="fa fa-filter mr-2"></i>
                      Filters
                    </h6>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleResetFilters}
                    >
                      <i className="fa fa-undo mr-1"></i>
                      Reset Filters
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-3">
                      <div className="form-group">
                        <label>Status</label>
                        <select
                          className="form-control"
                          value={filters.status}
                          onChange={(e) =>
                            handleFilterChange("status", e.target.value)
                          }
                        >
                          {statusOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-group">
                        <label>From Date</label>
                        <input
                          type="date"
                          className="form-control"
                          value={filters.dateFrom}
                          onChange={(e) =>
                            handleFilterChange("dateFrom", e.target.value)
                          }
                        />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-group">
                        <label>To Date</label>
                        <input
                          type="date"
                          className="form-control"
                          value={filters.dateTo}
                          onChange={(e) =>
                            handleFilterChange("dateTo", e.target.value)
                          }
                        />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-group">
                        <label>Invoice Search</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Invoice number or ID..."
                          value={filters.invoiceNumber}
                          onChange={(e) =>
                            handleFilterChange("invoiceNumber", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>

                  {/* Filter Results Info */}
                  <div className="mt-2">
                    <small className="text-muted">
                      Showing {filteredMetadata.length} of{" "}
                      {localPendingMetadata.length} transactions
                      {filters.status &&
                        ` • Status: ${statusOptions.find((opt) => opt.value === filters.status)?.label}`}
                      {filters.dateFrom && ` • From: ${filters.dateFrom}`}
                      {filters.dateTo && ` • To: ${filters.dateTo}`}
                      {filters.invoiceNumber &&
                        ` • Search: "${filters.invoiceNumber}"`}
                    </small>
                  </div>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="row mb-4">
                <div className="col-md-4">
                  <div className="card border-warning">
                    <div className="card-body text-center">
                      <h6 className="text-warning">Filtered Results</h6>
                      <h3 className="text-warning mb-0">{summary.total}</h3>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card border-info">
                    <div className="card-body text-center">
                      <h6 className="text-info">Total Amount</h6>
                      <h4 className="text-info mb-0">
                        {formatCurrency(summary.totalAmount)}
                      </h4>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card border-secondary">
                    <div className="card-body text-center">
                      <h6 className="text-secondary">Selected</h6>
                      <h3 className="text-secondary mb-0">
                        {selectedItems.length}
                      </h3>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bulk Actions */}
              {filters.status === "pending" && (
                <div className="card mb-3">
                  <div className="card-header">
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">Bulk Actions</h6>
                      <div className="btn-group">
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={handleSelectAll}
                          disabled={filteredMetadata.length === 0}
                        >
                          {selectedItems.length === filteredMetadata.length
                            ? "Deselect All"
                            : "Select All"}
                        </button>
                        <button
                          className="btn btn-success btn-sm"
                          onClick={handleBulkApproval}
                          disabled={
                            selectedItems.length === 0 ||
                            approvingLoading ||
                            !approvedBy
                          }
                        >
                          {approvingLoading ? (
                            <>
                              <span className="spinner-border spinner-border-sm mr-1"></span>
                              Approving...
                            </>
                          ) : (
                            <>
                              <i className="fa fa-check mr-1"></i>
                              Approve Selected ({selectedItems.length})
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Transactions Table */}
              <div className="card">
                <div className="card-header">
                  <h6 className="card-title mb-0">
                    {filters.status
                      ? `${statusOptions.find((opt) => opt.value === filters.status)?.label} Transactions`
                      : "All Transactions"}
                  </h6>
                </div>
                <div className="card-body">
                  {loading && filteredMetadata.length === 0 ? (
                    <div className="text-center py-4">
                      <div className="spinner-border" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                      <p className="mt-2">Loading transactions...</p>
                    </div>
                  ) : filteredMetadata.length === 0 ? (
                    <div className="text-center py-4">
                      <i className="fa fa-search fa-3x text-muted mb-3"></i>
                      <p className="text-muted">No transactions found</p>
                      <p className="text-sm text-muted">
                        Try adjusting your filters or check different date
                        ranges
                      </p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-striped table-hover">
                        <thead>
                          <tr>
                            {filters.status === "pending" && (
                              <th width="30">
                                <input
                                  type="checkbox"
                                  checked={
                                    selectedItems.length ===
                                      filteredMetadata.length &&
                                    filteredMetadata.length > 0
                                  }
                                  onChange={handleSelectAll}
                                />
                              </th>
                            )}
                            <th>Invoice</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>Wallet</th>
                            <th>Distribution</th>
                            <th>Created</th>
                            {filters.status === "pending" && <th>Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredMetadata.map((item) => (
                            <tr key={item._id}>
                              {filters.status === "pending" && (
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={selectedItems.includes(item._id)}
                                    onChange={() => handleSelectItem(item._id)}
                                  />
                                </td>
                              )}
                              <td>
                                <div>
                                  <strong>{item.invoiceNumber}</strong>
                                  <br />
                                  <small className="text-muted font-monospace">
                                    {item.invoiceId}
                                  </small>
                                </div>
                              </td>
                              <td>
                                <strong className="text-success">
                                  {formatCurrency(item.amount)}
                                </strong>
                              </td>
                              <td>
                                <span style={getStatusBadgeStyle(item.status)}>
                                  {item.status}
                                </span>
                              </td>
                              <td>
                                <div>
                                  <span className="badge badge-primary">
                                    {getWalletTypeDisplayName(item.walletType)}
                                  </span>
                                  <br />
                                  <small className="text-muted">
                                    Balance:{" "}
                                    {formatCurrency(item.walletAmount || 0)}
                                  </small>
                                </div>
                              </td>
                              <td>
                                <small className="d-block">
                                  Landlord:{" "}
                                  {formatCurrency(item.amountToLandlord)}
                                </small>
                                <small className="d-block">
                                  PM:{" "}
                                  {formatCurrency(item.amountToPropertyManager)}
                                </small>
                              </td>
                              <td className="text-sm">
                                {formatDate(item.createdAt)}
                              </td>
                              {filters.status === "pending" && (
                                <td>
                                  <div className="btn-group btn-group-sm">
                                    <button
                                      className="btn btn-success"
                                      onClick={() =>
                                        handleSingleApproval(item._id)
                                      }
                                      disabled={approvingLoading || !approvedBy}
                                      title="Approve"
                                    >
                                      <i className="fa fa-check"></i>
                                    </button>
                                    <button
                                      className="btn btn-danger"
                                      onClick={() => {
                                        setItemToReject(item);
                                        setRejectModalShow(true);
                                      }}
                                      disabled={approvingLoading || !approvedBy}
                                      title="Reject"
                                    >
                                      <i className="fa fa-times"></i>
                                    </button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <div className="d-flex justify-content-between align-items-center w-100">
                <small className="text-muted">
                  {filteredMetadata.length} transaction(s) • Total:{" "}
                  {formatCurrency(summary.totalAmount)}
                </small>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rejection Modal */}
      <RejectTransactionModal
        show={rejectModalShow}
        onClose={() => {
          setRejectModalShow(false);
          setItemToReject(null);
        }}
        onReject={handleRejectTransaction}
        item={itemToReject}
        loading={approvingLoading}
      />
    </>
  );
};

// Rejection Modal Component
const RejectTransactionModal = ({ show, onClose, onReject, item, loading }) => {
  const [rejectionReason, setRejectionReason] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!rejectionReason.trim()) {
      setError("Rejection reason is required");
      return;
    }

    onReject(item._id, rejectionReason.trim());
  };

  const handleClose = () => {
    setRejectionReason("");
    setError("");
    onClose();
  };

  if (!show || !item) return null;

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.7)" }}
    >
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header d-flex justify-content-between align-items-center py-3 px-4">
            <h5 className="modal-title text-danger">
              <i className="fa fa-times-circle mr-2"></i>
              Reject Transaction
            </h5>
            <button
              type="button"
              className="btn btn-light btn-sm rounded-circle p-2"
              onClick={onClose}
              style={{
                width: "40px",
                height: "40px",
                border: "1px solid #dee2e6",
                fontSize: "18px",
                lineHeight: "1",
              }}
            >
              <span className="text-muted">&times;</span>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="alert alert-warning">
                <strong>Invoice:</strong> {item.invoiceNumber}
                <br />
                <strong>Amount:</strong>{" "}
                {new Intl.NumberFormat("en-KE", {
                  style: "currency",
                  currency: "KES",
                }).format(item.amount)}
              </div>

              <div className="form-group">
                <label>
                  Rejection Reason <span className="text-danger">*</span>
                </label>
                <textarea
                  className={`form-control ${error ? "is-invalid" : ""}`}
                  rows="4"
                  placeholder="Please provide a reason for rejecting this transaction..."
                  value={rejectionReason}
                  onChange={(e) => {
                    setRejectionReason(e.target.value);
                    if (error) setError("");
                  }}
                  maxLength="500"
                />
                {error && <div className="invalid-feedback">{error}</div>}
                <small className="text-muted">
                  {rejectionReason.length}/500 characters
                </small>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-danger"
                disabled={loading || !rejectionReason.trim()}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-2"></span>
                    Rejecting...
                  </>
                ) : (
                  <>
                    <i className="fa fa-times mr-1"></i>
                    Reject Transaction
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PendingApprovalsModal;
